<template>
  <div class="page-container">
    <div :class="isMobile ? 'mobile-view' : 'desktop-view'">
      <div class="page-header">
        <button class="back-button" @click="goBack">
          <img src="/img/member-icon/back-button.svg" alt="back">
        </button>
        <h1 class="page-title">Edit Profile</h1>
      </div>

      <!-- Skeleton Loading State (only on first load) -->
      <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="account" />

      <form v-else @submit.prevent="submit()" class="account-form">
      <!-- Avatar Section -->
      <div class="avatar-section">
        <div class="avatar-preview">
          <img :src="(!imageErrorStatus && image.url) ? image.url : defaultAvatar" alt="Avatar" @error="imageError">
        </div>
        <div class="avatar-options">
          <span class="avatar-label">Choose Avatar</span>
          <div class="default-avatars">
            <img 
              v-for="(avatar, index) in defaultAvatars" 
              :key="index" 
              :src="avatar" 
              @click="selectDefaultAvatar(avatar)" 
              :class="{ 'selected': selectedAvatar === avatar }" 
              class="avatar-option"
            >
          </div>
          <span class="or-text">or upload your own</span>
          <input 
            type="file" 
            ref="fileInput" 
            @change="handleFileUpload" 
            accept="image/*" 
            class="file-input"
          >
          <button type="button" class="upload-btn" @click="$refs.fileInput.click()">
            Upload Photo
          </button>
        </div>
      </div>

      <!-- Form Fields -->
      <div class="form-section">
        <!-- Name Row -->
        <div class="form-row">
          <div class="form-group small">
            <label>Title</label>
            <select v-model="user.title" class="form-control">
              <option value="Mr.">Mr.</option>
              <option value="Mrs.">Mrs.</option>
              <option value="Miss">Miss</option>
            </select>
          </div>
          <div class="form-group">
            <label>First Name <span class="required">*</span></label>
            <input type="text" v-model="user.first_name" required class="form-control">
          </div>
          <div class="form-group">
            <label>Last Name <span class="required">*</span></label>
            <input type="text" v-model="user.last_name" required class="form-control">
          </div>
        </div>

        <!-- Contact Row -->
        <div class="form-row">
          <div class="form-group">
            <label>Phone <span class="required">*</span></label>
            <div class="phone-input">
              <input 
                v-if="!unknownCountry"
                type="text" 
                :value="countryCode" 
                readonly 
                disabled 
                class="form-control country-code"
              >
              <input 
                type="text" 
                v-model="user.phone" 
                required 
                class="form-control phone-number"
                inputmode="numeric"
                @input="handlePhoneInput"
                placeholder="Phone number"
              >
            </div>
          </div>
          <div class="form-group">
            <label>Email <span class="required">*</span></label>
            <input type="email" v-model="user.email" required class="form-control">
          </div>
        </div>

        <!-- Date of Birth Row -->
        <div class="form-row">
          <div class="form-group">
            <label>Date of Birth</label>
            <DatePicker
              v-model="user.date_of_birth"
              placeholder="Select Date of Birth"
              :max="maxDateOfBirth"
            />
          </div>
        </div>

        <!-- Location Row -->
        <div class="form-row">
          <div class="form-group">
            <label>Country <span class="required">*</span></label>
            <v-select
              v-model="user.country"
              :options="formattedCountries"
              :reduce="option => option.value"
              label="text"
              placeholder="-- Search & Select Country --"
              class="modern-v-select"
              :clearable="false"
            ></v-select>
          </div>
          <div class="form-group">
            <label>State/Province <span class="required">*</span></label>
            <input type="text" v-model="user.state" required class="form-control">
          </div>
        </div>

        <!-- City Row -->
        <div class="form-row">
          <div class="form-group">
            <label>City/Area <span class="required">*</span></label>
            <input type="text" v-model="user.city" required class="form-control">
          </div>
          <div class="form-group">
            <label>Post Code <span class="required">*</span></label>
            <input type="text" v-model="user.postcode" required class="form-control">
          </div>
        </div>

        <!-- Address -->
        <div class="form-row">
          <div class="form-group full">
            <label>Address</label>
            <textarea v-model="user.address" class="form-control" rows="3" placeholder="Enter your address"></textarea>
          </div>
        </div>

        <!-- Preference -->
        <div class="form-row">
          <div class="form-group full">
            <label>Preferences</label>
            <textarea v-model="user.preference" class="form-control" rows="3" placeholder="Enter your preferences (e.g. dietary restrictions, room preference, etc.)"></textarea>
          </div>
        </div>
      </div>

      <!-- Notification Preferences -->
      <div class="form-section notification-section">
        <h3 class="section-title">Notification Preferences</h3>
        <div class="toggle-row">
          <div class="toggle-info">
            <span class="toggle-label">Newsletter Email</span>
            <span class="toggle-description">Receive latest news, updates, and promotions via email</span>
          </div>
          <label class="toggle-switch">
            <input 
              type="checkbox" 
              v-model="newsletterSubscribed" 
            >
            <span class="toggle-slider"></span>
          </label>
        </div>
             <div class="danger-zone-section">
        <div class="danger-zone-header">
          <div class="danger-zone-text">
            <h3 class="danger-zone-title">Delete Account</h3>
            <p class="danger-zone-desc">Permanently remove your account, profile data, points, and benefits. This action cannot be undone.</p>
          </div>
          <button 
            type="button" 
            class="btn-delete-trigger" 
            @click="openDeleteModal"
          >
            <svg class="delete-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
            Delete Account
          </button>
        </div>
      </div>
      </div>
   

      <!-- Action Buttons -->
      <div class="form-actions">
        <button type="button" class="btn-cancel" @click="cancel">Cancel</button>
        <button type="submit" class="btn-save">Save Changes</button>
      </div>
    </form>
    </div>

    <!-- Delete Account Confirmation Modal -->
    <o-warning-box
      v-if="showDeleteModal"
      title="Confirm Delete"
      :item-name="accountToDeleteName"
      warning-message="This action cannot be undone."
      confirm-text="Delete"
      :is-loading="isDeleting"
      @confirm="handleDeleteAccount"
      @cancel="closeDeleteModal"
      :hide-action="false"
    />

    <!-- Toast Notification -->
    <ToastNotification
      v-model="showToast"
      :type="toastType"
      :title="toastTitle"
      :message="toastMessage"
      :duration="3000"
      position="top-right"
    />
  </div>
</template>


<script>
import { computed, onMounted, reactive, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import SkeletonLoader from '@/components/member/SkeletonLoader.vue';
import ToastNotification from '@/components/member/ToastNotification.vue';
import DatePicker from '@/components/DatePicker.vue';
import api from '@/helpers/api';
import { setSwipeBackEnabled } from '@/services/swipeBack';
import { OWarningBox } from '@/components/OmnihControl';

export default {
  name: 'MemberAccount',
  components: {
    vSelect,
    SkeletonLoader,
    ToastNotification,
    DatePicker,
    OWarningBox
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const page = reactive({
      isLoading: false,
      isFirstLoad: true
    });

    const user = reactive({
      title: '',
      first_name: '',
      last_name: '',
      id: '',
      email: '',
      avatar: '',
      phone: '',
      address: '',
      state: '',
      country: null,
      city: '',
      postcode: '',
      date_of_birth: '',
      preference: ''
    });

    const image = reactive({ url: '' });
    const imageErrorStatus = ref(false);
    function imageError() {
      imageErrorStatus.value = true;
    }

    watch(() => image.url, () => {
      imageErrorStatus.value = false;
    });

    const displayMemberName = computed(() => {
      const u = store.state.Auth.userInfo;
      const firstName = user.first_name || u?.first_name || '';
      const lastName = user.last_name || u?.last_name || '';
      if (firstName || lastName) {
        return `${firstName} ${lastName}`.trim();
      }
      return u?.name || u?.email || '-';
    });

    const defaultAvatar = computed(() => {
      const title = user.title || store.state.Auth.userInfo?.title || '';
      return title === 'Mr.'
        ? 'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/man.png'
        : 'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/woman.png';
    });

    const getInitials = computed(() => {
      const name = displayMemberName.value;
      if (!name || name === '-') return '?';
      const words = name.split(' ').filter(Boolean);
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    });
    const countries = ref([]);
    const countryCode = ref('');
    const unknownCountry = ref(false);
    const userCountry = ref();
    const selectedAvatar = ref(null);
    const fileInput = ref(null);
    const isMobile = ref(window.innerWidth < 992);

    // Toast state
    const showToast = ref(false);
    const toastType = ref('success');
    const toastTitle = ref('');
    const toastMessage = ref('');

    window.addEventListener('resize', () => {
      isMobile.value = window.innerWidth < 992;
    });

    const defaultAvatars = [
      'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/woman.png',
      'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/man.png'
    ];

    const currUser = computed(() => store.state.Auth.currUser);

    watch(() => user.country, updatePhoneCode);
    watch(() => currUser.value.id, () => { loadState(); });

    onMounted(async () => {
      page.isLoading = true;
      await loadCountries();
      await loadState();
      await loadSubscriptionStatus();
      page.isLoading = false;
      page.isFirstLoad = false;
    });

    const formattedCountries = computed(() => {
      return countries.value.map(c => ({
        value: Number(c.value),
        text: `${c.text} (${c.phone_code ? c.phone_code : '-'})`
      }));
    });

    async function loadCountries() {
      try {
        const response = await store.dispatch('Countries/read');
        countries.value = response.map(country => ({ 
          value: country.id, 
          text: country.name,
          phone_code: country.phone_code 
        }));
      } catch (err) {
        console.error(err);
      }
    }

    function updatePhoneCode() {
      if (!countries.value || countries.value.length === 0 || !user.country) return;

      const country = countries.value.find(c => Number(c.value) === Number(user.country));
      
      if (country && country.phone_code) {
        unknownCountry.value = false;
        countryCode.value = country.phone_code;
        userCountry.value = country;
      } else {
        unknownCountry.value = true;
        countryCode.value = '';
        console.error(`Phone code not found for country ID: ${user.country}`);
      }
    }

    async function loadState() {
      const id = currUser.value.id;
      if (!id) return;

      try {
        const data = await store.dispatch('User/read_single', id);
        user.country = data.user_profile.country ? Number(data.user_profile.country) : null;
        user.id = data.id;
        user.title = data.title;
        user.first_name = data.first_name;
        user.last_name = data.last_name;
        user.email = data.email;
        user.phone = removeCountryCode(data.user_profile.phone);
        user.address = data.user_profile.address;
        user.state = data.user_profile.state;
        user.city = data.user_profile.city;
        user.postcode = data.user_profile.postcode;
        user.date_of_birth = data.user_profile.date_of_birth ? data.user_profile.date_of_birth.split('T')[0].split(' ')[0] : '';
        user.preference = data.user_profile.preference;
        image.url = data.user_profile.avatar?.image_url || '';
      } catch (err) {
        console.error(err);
      }
    }

    function removeCountryCode(phone) {
      if (!phone || user.country === null) return phone;

      const country = countries.value.find(c => Number(c.value) === Number(user.country));
      if (country && country.phone_code) {
        userCountry.value = country;
        
        // Normalize phone code (remove + if present)
        const cleanCode = country.phone_code.replace(/^\+/, '');
        
        // Check if phone starts with +62...
        if (phone.startsWith('+' + cleanCode)) {
          return phone.substring(cleanCode.length + 1).trim();
        } 
        // Check if phone starts with 62...
        else if (phone.startsWith(cleanCode)) {
          return phone.substring(cleanCode.length).trim();
        }
      }
      return phone;
    }

    function selectDefaultAvatar(avatar) {
      user.avatar = avatar;
      image.url = avatar;
      selectedAvatar.value = avatar;
    }

    function handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          user.avatar = e.target.result;
          image.url = e.target.result;
          selectedAvatar.value = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    }

    /**
     * Handle phone input to allow only numeric characters
     */
    function handlePhoneInput(event) {
      const value = event.target.value;
      // Remove all non-numeric characters
      const numericValue = value.replace(/[^0-9]/g, '');
      user.phone = numericValue;
    }

    function submit() {
      const data = { ...user };
      
      // Clean up phone number first (remove any spaces)
      let cleanPhone = data.phone.trim();
      
      // Check if country code is already prefixed to prevent double prefixing
      // Normalize codes for comparison (remove +)
      const cleanCountryCode = countryCode.value.replace(/^\+/, '');
      
      if (!cleanPhone.startsWith(cleanCountryCode) && !cleanPhone.startsWith('+' + cleanCountryCode)) {
         data.phone = countryCode.value + cleanPhone;
      } else if (cleanPhone.startsWith(cleanCountryCode)) {
         // Even if it starts with 62, we might want to ensure it has + if backend expects it
         // But usually backend expects +62 or just 62. Let's assume we prepend countryCode which usually has +
         // if countryCode has +, and phone starts with 62, we should prepend +?
         // Safer approach: if it starts with 62, just ensure format matches countryCode
         if (countryCode.value.startsWith('+') && !cleanPhone.startsWith('+')) {
            data.phone = '+' + cleanPhone;
         } else {
             data.phone = cleanPhone;
         }
      } else {
         data.phone = cleanPhone;
      }
      
      page.isLoading = true;

      if (data.avatar === '') {
        delete data.avatar;
      }

      store.dispatch('User/update', { data })
        .then(async () => {
          if (newsletterSubscribed.value !== initialNewsletterSubscribed.value) {
            try {
              await api.request('post', '/api/client/newsletter-subscription/toggle');
              initialNewsletterSubscribed.value = newsletterSubscribed.value;
            } catch (err) {
              console.error('Failed to update newsletter preference:', err);
            }
          }
          store.dispatch('Auth/auth_check');
          // Show success toast
          toastType.value = 'success';
          toastTitle.value = 'Success';
          toastMessage.value = 'Profile updated successfully!';
          showToast.value = true;
          
          // Redirect after toast shows
          setTimeout(() => {
            router.push('/client/benefit');
          }, 1500);
        })
        .catch((err) => {
          console.error(err);
          // Show error toast
          toastType.value = 'error';
          toastTitle.value = 'Error';
          toastMessage.value = 'Failed to update profile. Please try again.';
          showToast.value = true;
        })
        .finally(() => {
          page.isLoading = false;
        });
    }

    function cancel() {
      router.push('/client/benefit');
    }

    function goBack() {
      router.push('/client/benefit');
    }

    // Newsletter subscription state
    const newsletterSubscribed = ref(true);
    const initialNewsletterSubscribed = ref(true);

    async function loadSubscriptionStatus() {
      try {
        const response = await api.request('get', '/api/client/newsletter-subscription');
        if (response && response.data && response.data.data) {
          newsletterSubscribed.value = response.data.data.is_subscribed;
          initialNewsletterSubscribed.value = response.data.data.is_subscribed;
        }
      } catch (err) {
        console.warn('Failed to load subscription status:', err);
      }
    }

    const maxDateOfBirth = computed(() => {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    });

    // Delete account state
    const showDeleteModal = ref(false);
    const isDeleting = ref(false);

    const accountToDeleteName = computed(() => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      return fullName || 'your account';
    });

    function openDeleteModal() {
      showDeleteModal.value = true;
    }

    function closeDeleteModal() {
      if (isDeleting.value) return;
      showDeleteModal.value = false;
    }

    async function handleDeleteAccount() {
      if (isDeleting.value) return;

      isDeleting.value = true;
      try {
        await api.request('delete', '/api/client/account');

        await store.dispatch('Auth/logout');

        await setSwipeBackEnabled(false);

        const newAuthFlag = store.getters['FeatureFlag/getNewAuthModuleFlag'];
        const targetRoute = newAuthFlag === 'ACTIVE' ? '/member/login' : '/login';

        router.replace(targetRoute);
      } catch (err) {
        console.error('[MemberAccount] Failed to delete account:', err);
        toastType.value = 'error';
        toastTitle.value = 'Deletion Failed';
        toastMessage.value = err?.response?.data?.message || err?.message || 'Failed to delete account. Please try again.';
        showToast.value = true;
        isDeleting.value = false;
        closeDeleteModal();
      }
    }

    return {
      page,
      user,
      image,
      defaultAvatar,
      imageErrorStatus,
      imageError,
      getInitials,
      countries,
      formattedCountries,
      countryCode,
      unknownCountry,
      defaultAvatars,
      selectedAvatar,
      fileInput,
      selectDefaultAvatar,
      handleFileUpload,
      handlePhoneInput,
      submit,
      cancel,
      goBack,
      isMobile,
      // Toast
      showToast,
      toastType,
      toastTitle,
      toastMessage,
      // Newsletter subscription
      newsletterSubscribed,
      // Date of Birth
      maxDateOfBirth,
      // Delete account
      showDeleteModal,
      isDeleting,
      accountToDeleteName,
      openDeleteModal,
      closeDeleteModal,
      handleDeleteAccount,
    };
  }
};
</script>


<style scoped>
.page-container {
  width: 100%;
  min-height: 100vh;
}

.desktop-view {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
}

.desktop-view .page-header {
  margin-bottom: 32px;
}

.desktop-view .page-title {
  font-size: 24px;
}

.desktop-view .avatar-section {
  flex-direction: row;
  justify-content: flex-start;
  gap: 32px;
}

.desktop-view .avatar-preview {
  width: 120px;
  height: 120px;
}

.desktop-view .avatar-options {
  align-items: flex-start;
}

.desktop-view .form-row {
  flex-direction: row;
}

.desktop-view .form-group.small {
  flex: 0 0 100px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.back-button {
  border: none;
  padding: 10px;
  background-color: var(--primary-color, #F93E3E);
  border-radius: 12px;
  line-height: 0;
  cursor: pointer;
}

.back-button img {
  width: 18px;
  height: 18px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Loading removed - using skeleton instead */

.account-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
}

.avatar-preview {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--primary-border, #F93E3E);
}

.avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-preview .member-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-gradient, linear-gradient(135deg, #F93E3E 0%, #E02020 100%));
  color: #ffffff;
  font-size: 32px;
  font-weight: 600;
  user-select: none;
}

.avatar-options {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.avatar-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.default-avatars {
  display: flex;
  gap: 12px;
}

.avatar-option {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s;
}

.avatar-option:hover {
  border-color: var(--primary-border, #F93E3E);
}

.avatar-option.selected {
  border-color: var(--primary-border, #F93E3E);
}

.or-text {
  font-size: 12px;
  color: #717784;
}

.file-input {
  display: none;
}

.upload-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid var(--primary-border, #F93E3E);
  color: var(--primary-color, #F93E3E);
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.upload-btn:hover {
  background: var(--secondary-color, #FFF5F5);
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group.small {
  flex: 0 0 80px;
}

.form-group.full {
  flex: 1 1 100%;
}

.form-group label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
}

.required {
  color: var(--danger-color, #F93E3E);
}

.form-control {
  padding: 10px 12px;
  border: 1px solid #E1E4EA;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: var(--primary-border, #F93E3E);
}

.form-control:disabled {
  background: #F5F5F5;
  color: #717784;
}

select.form-control {
  cursor: pointer;
}

textarea.form-control {
  resize: none;
}

.phone-input {
  display: flex;
  gap: 8px;
}

.country-code {
  flex: 0 0 60px;
  text-align: center;
}

.phone-number {
  flex: 1;
}

.form-actions {
  display: flex;
  gap: 12px;
  padding: 20px;
  background: white;
  border-radius: 12px;
}

.btn-cancel,
.btn-save {
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-cancel {
  background: white;
  border: 1px solid #E1E4EA;
  color: #717784;
}

.btn-save {
  background: var(--primary-color, #F93E3E);
  border: none;
  color: white;
}

.btn-cancel:hover,
.btn-save:hover {
  opacity: 0.9;
}

@media (max-width: 480px) {
  .form-row {
    flex-direction: column;
  }
  
  .form-group.small {
    flex: 1;
  }
}

/* Notification Preferences Section */
.notification-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #E1E4EA;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1A1A2E;
  margin: 0 0 16px;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #f8f9fb;
  border-radius: 10px;
  border: 1px solid #E1E4EA;
}

.toggle-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toggle-label {
  font-size: 14px;
  font-weight: 500;
  color: #1A1A2E;
}

.toggle-description {
  font-size: 12px;
  color: #717784;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cdd1d9;
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider::before {
  content: "";
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.toggle-switch input:checked + .toggle-slider {
  background-color: var(--primary-color, #F93E3E);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-switch input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

:deep(.modern-v-select) {
  --vs-border-color: #cbd5e1;
  --vs-border-radius: 8px;
  --vs-font-size: 14px;
  --vs-colors--light: #64748b;
  text-align: left;
}

:deep(.modern-v-select .vs__dropdown-toggle) {
  background-color: #ffffff;
  border: 1px solid var(--vs-border-color);
  border-radius: var(--vs-border-radius);
  padding: 6px 4px;
  min-height: 42px;
  margin: 0 !important;
  transition: all 0.2s ease;
}

:deep(.modern-v-select.vs--open .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-toggle:focus-within) {
  background-color: #ffffff;
  border-color: var(--primary-color, #f93e3e);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}

:deep(.modern-v-select .vs__search::placeholder),
:deep(.modern-v-select .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-menu) {
  font-family: inherit;
  font-size: 14px;
  color: #334155;
}

:deep(.modern-v-select .vs__clear),
:deep(.modern-v-select .vs__open-indicator) {
  fill: #94a3b8;
}

:deep(.modern-v-select .vs__dropdown-menu) {
  border-color: #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  margin-top: 4px;
  padding: 4px 0;
  max-height: 200px;
}

:deep(.modern-v-select .vs__dropdown-option) {
  padding: 8px 16px;
  color: #334155;
}

:deep(.modern-v-select .vs__dropdown-option--highlight) {
  background: var(--primary-color, #f93e3e);
  color: #ffffff;
}

/* Allow vs__selected to take full width when closed */
:deep(.modern-v-select .vs__selected-options) {
  flex-wrap: nowrap !important;
  overflow: hidden;
  align-items: center;
}

:deep(.modern-v-select .vs__selected) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 !important;
  padding: 0 6px !important;
  align-self: center;
  color: #334155;
  font-size: 14px;
}

:deep(.modern-v-select .vs__search),
:deep(.modern-v-select .vs__search:focus) {
  border: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
  font-size: 14px !important;
  color: #0f172a !important;
  outline: none !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 auto !important;
}

:deep(.modern-v-select.vs--open .vs__search),
:deep(.modern-v-select.vs--searching .vs__search) {
  flex: 1 1 auto !important;
  width: auto !important;
  min-width: 60px !important;
  padding: 4px 6px !important;
}

:deep(.modern-v-select.vs--searching .vs__selected) {
  display: none !important;
}

:deep(.date-input-trigger) {
  padding: 10px 12px;
  border: 1px solid #E1E4EA;
  border-radius: 8px;
  min-height: 42px;
}

:deep(.date-input-trigger .selected-date) {
  font-size: 14px;
}

:deep(.date-input-trigger.has-value .selected-date) {
  color: #1A1A2E;
}


.danger-zone-section {
  padding: 20px;
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.danger-zone-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.danger-zone-text {
  flex: 1;
}

.danger-zone-title {
  font-size: 15px;
  font-weight: 600;
  color: #DC2626;
  margin: 0 0 4px;
}

.danger-zone-desc {
  font-size: 13px;
  color: #6B7280;
  margin: 0;
  line-height: 1.4;
}

.btn-delete-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #DC2626;
  border: none;
  color: #FFFFFF;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

.btn-delete-trigger:hover {
  background: #DC2626;
  color: #FFFFFF;
}

.btn-delete-trigger:active {
  transform: scale(0.98);
}

.delete-icon {
  width: 15px;
  height: 15px;
}

@media (max-width: 640px) {
  .danger-zone-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .btn-delete-trigger {
    width: 100%;
    justify-content: center;
    padding: 12px;
  }
}

</style>
